
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '../api/client';
import { voicesApi, type VoicesListParams } from '../api/endpoints';
import type {
  Id,
  MediaLink,
  OkResponse,
  Page,
  ToolTask,
  Voice,
  VoiceCreateRequest,
  VoiceImportRequest,
  VoiceImportSpec,
  VoiceUpdateRequest,
} from '../types/api';
import { queryKeys } from './keys';
import { renewDelayMs } from './useMedia';
import { useTaskTracker, type TaskTracker } from './useTasks';

export function useVoices(params: VoicesListParams = {}): UseQueryResult<Page<Voice>, ApiError> {
  return useQuery<Page<Voice>, ApiError>({
    queryKey: queryKeys.voices(params),
    queryFn: () => voicesApi.list(params),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useCharacterVoices(
  characterId: Id | undefined,
): UseQueryResult<Page<Voice>, ApiError> {
  return useQuery<Page<Voice>, ApiError>({
    queryKey: queryKeys.voices({ character_id: characterId, limit: 200 }),
    queryFn: () => voicesApi.list({ character_id: characterId as Id, limit: 200 }),
    enabled: Boolean(characterId),
    staleTime: 60_000,
  });
}

export function useVoice(voiceId: Id | undefined): UseQueryResult<Voice, ApiError> {
  return useQuery<Voice, ApiError>({
    queryKey: queryKeys.voice(voiceId ?? ''),
    queryFn: () => voicesApi.get(voiceId as Id),
    enabled: Boolean(voiceId),
    staleTime: 60_000,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });
}

export function useVoiceSample(
  voiceId: Id | undefined,
  enabled = true,
): UseQueryResult<MediaLink, ApiError> {
  return useQuery<MediaLink, ApiError>({
    queryKey: queryKeys.voiceSample(voiceId ?? ''),
    queryFn: () => voicesApi.sample(voiceId as Id),
    enabled: Boolean(voiceId) && enabled,
    staleTime: Infinity,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => renewDelayMs(query.state.data?.expires_at),
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });
}

export function useVoiceImportSpec(
  options: { enabled?: boolean } = {},
): UseQueryResult<VoiceImportSpec, ApiError> {
  return useQuery<VoiceImportSpec, ApiError>({
    queryKey: queryKeys.voiceImportSpec,
    queryFn: () => voicesApi.importSpec(),
    enabled: options.enabled ?? true,
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}

function useVoiceInvalidation() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.voicesRoot });
    void qc.invalidateQueries({ queryKey: queryKeys.charactersRoot });
  };
}

export function useCreateVoice() {
  const invalidate = useVoiceInvalidation();
  return useMutation<Voice, ApiError, VoiceCreateRequest>({
    mutationFn: (payload) => voicesApi.create(payload),
    retry: false,
    onSuccess: invalidate,
  });
}

export function useUpdateVoice(voiceId: Id) {
  const qc = useQueryClient();
  const invalidate = useVoiceInvalidation();
  return useMutation<Voice, ApiError, VoiceUpdateRequest>({
    mutationFn: (payload) => voicesApi.update(voiceId, payload),
    onSuccess: (voice) => {
      qc.setQueryData(queryKeys.voice(voiceId), voice);
      invalidate();
    },
  });
}

export function useDeleteVoice() {
  const qc = useQueryClient();
  const invalidate = useVoiceInvalidation();
  return useMutation<OkResponse, ApiError, Id>({
    mutationFn: (voiceId) => voicesApi.remove(voiceId),
    onSuccess: (_result, voiceId) => {
      qc.removeQueries({ queryKey: queryKeys.voice(voiceId) });
      qc.removeQueries({ queryKey: queryKeys.voiceSample(voiceId) });
      invalidate();
    },
  });
}

export interface VoiceClone {
  clone: UseMutationResult<ToolTask, ApiError, Id>;
  task: TaskTracker;
}

export function useCloneVoice(): VoiceClone {
  const qc = useQueryClient();
  const task = useTaskTracker([queryKeys.voicesRoot, queryKeys.charactersRoot]);
  const clone = useMutation<ToolTask, ApiError, Id>({
    mutationFn: (voiceId) => voicesApi.clone(voiceId),
    retry: false,
    onSuccess: (accepted) => {
      task.track(accepted.id);
      void qc.invalidateQueries({ queryKey: queryKeys.tasksRoot });
    },
  });
  return { clone, task };
}

export interface VoiceImport {
  start: UseMutationResult<ToolTask, ApiError, VoiceImportRequest>;
  task: TaskTracker;
}

export function useImportVoices(): VoiceImport {
  const qc = useQueryClient();
  const task = useTaskTracker([queryKeys.voicesRoot, queryKeys.charactersRoot]);
  const start = useMutation<ToolTask, ApiError, VoiceImportRequest>({
    mutationFn: (payload) => voicesApi.import(payload),
    retry: false,
    onSuccess: (accepted) => {
      task.track(accepted.id);
      void qc.invalidateQueries({ queryKey: queryKeys.tasksRoot });
    },
  });
  return { start, task };
}

export function usableVoices(voices: readonly Voice[] | undefined): Voice[] {
  return (voices ?? []).filter((voice) => voice.usable);
}

const VOICE_STATUS_LABELS: Readonly<Record<Voice['status'], string>> = {
  pending: 'клонируется',
  ready: 'готов',
  failed: 'ошибка клонирования',
  evicted: 'вытеснен, нужно переклонировать',
};

export function describeVoiceStatus(voice: Voice | undefined): string {
  if (!voice) return '';
  return VOICE_STATUS_LABELS[voice.status] ?? voice.status;
}
